import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, X, Trash2 } from "lucide-react";

import { useLang } from "@/context/LanguageContext";
import { fetchArticles, createArticle, deleteArticle } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border border-white/10 text-[#F0F4F8] placeholder:text-[#94A3B8]/40 px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-precision transition-colors";
const labelCls = "block font-mono-tech text-[10px] uppercase tracking-widest text-[#94A3B8] mb-1.5";

const EMPTY = {
  slug: "", category: "", cover: "", author: "Tim Litterantara", read_minutes: 5, tags: "",
  title_id: "", title_en: "", excerpt_id: "", excerpt_en: "", body_id: "", body_en: "",
};

export default function AdminInsights() {
  const { t } = useLang();
  const a = t.admin;
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetchArticles().then(setArticles).catch(() => setArticles([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.slug || !form.category || !form.title_id || !form.title_en || !form.body_id || !form.body_en) {
      toast.error("Lengkapi field wajib.");
      return;
    }
    setSaving(true);
    try {
      await createArticle({
        slug: form.slug.trim().toLowerCase().replace(/\s+/g, "-"),
        category: form.category.trim(),
        cover: form.cover.trim(),
        author: form.author.trim() || "Tim Litterantara",
        read_minutes: Number(form.read_minutes) || 5,
        tags: form.tags.split(",").map((x) => x.trim()).filter(Boolean),
        title: { id: form.title_id, en: form.title_en },
        excerpt: { id: form.excerpt_id, en: form.excerpt_en },
        body: { id: form.body_id, en: form.body_en },
      });
      toast.success(a.articleCreated);
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat artikel.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(a.deleteConfirm)) return;
    try {
      await deleteArticle(slug);
      toast.success(a.articleDeleted);
      load();
    } catch (err) {
      toast.error("Gagal menghapus.");
    }
  };

  return (
    <div data-testid="admin-insights">
      <div className="flex items-center justify-between mb-6">
        <p className="text-[#94A3B8] text-sm">{articles.length} artikel</p>
        <button
          data-testid="new-article-btn"
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-1.5 font-mono-tech text-[11px] uppercase tracking-widest bg-cyan-precision text-navy px-4 py-2.5 hover:bg-cyan-bright transition-colors"
        >
          <Plus size={14} /> {a.newArticle}
        </button>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm" data-testid="articles-table">
          <thead>
            <tr className="border-b border-white/10 text-left">
              {[a.colTitle, a.colCategory, a.colDate, ""].map((h, i) => (
                <th key={i} className="font-mono-tech text-[10px] uppercase tracking-widest text-[#94A3B8] px-5 py-4">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-5 py-12 text-center text-[#94A3B8]">…</td></tr>
            ) : articles.length === 0 ? (
              <tr><td colSpan={4} className="px-5 py-12 text-center text-[#94A3B8]">{a.tabInsights}: 0</td></tr>
            ) : (
              articles.map((art) => (
                <tr key={art.slug} data-testid={`article-row-${art.slug}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors">
                  <td className="px-5 py-4 text-[#F0F4F8] max-w-md">{art.title.en}</td>
                  <td className="px-5 py-4">
                    <span className="font-mono-tech text-[10px] uppercase tracking-widest text-cyan-precision border border-cyan-precision/30 px-2 py-1">{art.category}</span>
                  </td>
                  <td className="px-5 py-4 text-[#94A3B8] font-mono-tech text-xs whitespace-nowrap">{art.date}</td>
                  <td className="px-5 py-4 text-right">
                    <button
                      data-testid={`delete-article-${art.slug}`}
                      onClick={() => remove(art.slug)}
                      className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-widest text-red-300 border border-red-300/30 px-3 py-1.5 hover:bg-red-300/10 transition-colors"
                    >
                      <Trash2 size={12} /> {a.delete}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create form drawer */}
      <AnimatePresence>
        {showForm && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowForm(false)} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" />
            <motion.aside
              data-testid="article-form"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-xl bg-navy-surface border-l border-white/10 z-50 overflow-y-auto"
            >
              <div className="p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-display text-xl">{a.createArticle}</h2>
                  <button onClick={() => setShowForm(false)} className="text-[#94A3B8] hover:text-[#F0F4F8]"><X size={20} /></button>
                </div>
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className={labelCls}>{a.fSlug} *</label><input data-testid="art-slug" className={inputCls} value={form.slug} onChange={set("slug")} /></div>
                    <div><label className={labelCls}>{a.fCategory} *</label><input data-testid="art-category" className={inputCls} value={form.category} onChange={set("category")} placeholder="LLM / Security / Strategy" /></div>
                  </div>
                  <div><label className={labelCls}>{a.fCover}</label><input className={inputCls} value={form.cover} onChange={set("cover")} placeholder="https://…" /></div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><label className={labelCls}>{a.fAuthor}</label><input className={inputCls} value={form.author} onChange={set("author")} /></div>
                    <div><label className={labelCls}>{a.fRead}</label><input type="number" className={inputCls} value={form.read_minutes} onChange={set("read_minutes")} /></div>
                    <div><label className={labelCls}>{a.fTags}</label><input className={inputCls} value={form.tags} onChange={set("tags")} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className={labelCls}>{a.fTitleId} *</label><input data-testid="art-title-id" className={inputCls} value={form.title_id} onChange={set("title_id")} /></div>
                    <div><label className={labelCls}>{a.fTitleEn} *</label><input data-testid="art-title-en" className={inputCls} value={form.title_en} onChange={set("title_en")} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className={labelCls}>{a.fExcerptId}</label><textarea rows={2} className={`${inputCls} resize-none`} value={form.excerpt_id} onChange={set("excerpt_id")} /></div>
                    <div><label className={labelCls}>{a.fExcerptEn}</label><textarea rows={2} className={`${inputCls} resize-none`} value={form.excerpt_en} onChange={set("excerpt_en")} /></div>
                  </div>
                  <p className="font-mono-tech text-[10px] text-[#94A3B8]/70">{a.bodyHint}</p>
                  <div><label className={labelCls}>{a.fBodyId} *</label><textarea data-testid="art-body-id" rows={6} className={`${inputCls} resize-none font-mono-tech text-xs`} value={form.body_id} onChange={set("body_id")} /></div>
                  <div><label className={labelCls}>{a.fBodyEn} *</label><textarea data-testid="art-body-en" rows={6} className={`${inputCls} resize-none font-mono-tech text-xs`} value={form.body_en} onChange={set("body_en")} /></div>
                  <div className="flex gap-3 pt-2">
                    <button data-testid="save-article" type="submit" disabled={saving}
                      className="inline-flex items-center gap-2 font-mono-tech text-[12px] uppercase tracking-widest bg-cyan-precision text-navy px-6 py-3 hover:bg-cyan-bright transition-colors disabled:opacity-50">
                      {saving ? "…" : a.save}
                    </button>
                    <button type="button" onClick={() => setShowForm(false)}
                      className="font-mono-tech text-[12px] uppercase tracking-widest border border-white/10 text-[#94A3B8] px-6 py-3 hover:text-[#F0F4F8] transition-colors">
                      {a.cancel}
                    </button>
                  </div>
                </form>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
